<template>
  <div class="tp-wrapper" ref="wrapper">
    <button
      type="button"
      class="tp-trigger"
      :class="{ 'tp-trigger-disabled': disabled }"
      :disabled="disabled"
      ref="trigger"
      @click="togglePicker"
    >
      <i class="fas fa-clock tp-clock-icon"></i>
      {{ displayTime }}
    </button>

    <transition name="tp-fade">
      <div v-if="isOpen" class="tp-dropdown" ref="dropdown">
        <!-- Header -->
        <div class="tp-header">
          {{ displayTime }}
        </div>

        <!-- Columns -->
        <div class="tp-body">
          <!-- Hour Column -->
          <div class="tp-column">
            <div class="tp-col-label">Hour</div>
            <div class="tp-scroll" ref="hourScroll">
              <div
                v-for="h in validHours"
                :key="h"
                class="tp-item"
                :class="{ 'tp-item-active': selectedHour === h }"
                @click="selectHour(h)"
              >
                {{ formatHourLabel(h) }}
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div class="tp-colon">:</div>

          <!-- Minute Column -->
          <div class="tp-column">
            <div class="tp-col-label">Min</div>
            <div class="tp-scroll" ref="minScroll">
              <div
                v-for="m in validMinutes"
                :key="m"
                class="tp-item"
                :class="{ 'tp-item-active': selectedMinute === m }"
                @click="selectMinute(m)"
              >
                {{ String(m).padStart(2, '0') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="tp-footer">
          <button type="button" class="tp-done-btn" @click="closePicker">Done</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'TimePicker',
  props: {
    value: {
      type: String,
      default: '09:00',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      selectedHour: 9,
      selectedMinute: 0,
    };
  },
  computed: {
    // 1 AM (01) → 11 PM (23) → 12 AM (00) — full 24-hour cycle, midnight last
    validHours() {
      return [...Array.from({ length: 23 }, (_, i) => i + 1), 0];
    },
    // All 60 minutes
    validMinutes() {
      return Array.from({ length: 60 }, (_, i) => i);
    },
    displayTime() {
      const h = this.selectedHour;
      const m = this.selectedMinute;
      if (h === 0) return `12:${String(m).padStart(2, '0')} AM`; // midnight
      const period = h >= 12 ? 'PM' : 'AM';
      const hour12 = h > 12 ? h - 12 : h;
      return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
    },
  },
  watch: {
    value: {
      handler(val) {
        this.parseValue(val);
      },
      immediate: true,
    },
  },
  methods: {
    parseValue(val) {
      if (!val) return;
      const parts = String(val).split(':');
      let h = parseInt(parts[0], 10);
      const m = parseInt(parts[1] || '0', 10);
        if (isNaN(h) || h < 0) h = 9;
      if (h > 23) h = 23;
      this.selectedHour = h;
      this.selectedMinute = isNaN(m) ? 0 : Math.min(59, Math.max(0, m));
    },

    formatHourLabel(h) {
      if (h === 0)  return '12 AM'; // midnight — end of day
      if (h === 12) return '12 PM';
      if (h > 12)   return `${h - 12} PM`;
      return `${h} AM`;
    },

    togglePicker() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        document.addEventListener('mousedown', this.handleOutsideClick);
        this.$nextTick(() => {
          this.positionDropdown();
          this.scrollToSelected();
        });
      } else {
        document.removeEventListener('mousedown', this.handleOutsideClick);
      }
    },

    closePicker() {
      this.isOpen = false;
      document.removeEventListener('mousedown', this.handleOutsideClick);
    },

    handleOutsideClick(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.closePicker();
      }
    },

    positionDropdown() {
      const trigger = this.$refs.trigger;
      const dropdown = this.$refs.dropdown;
      if (!trigger || !dropdown) return;

      const rect = trigger.getBoundingClientRect();
      const dropH = dropdown.offsetHeight || 290;
      const dropW = dropdown.offsetWidth || 224;

      // Default: open below the trigger, horizontally centered
      let top = rect.bottom + 6;
      let left = rect.left + rect.width / 2 - dropW / 2;

      // Flip above if not enough space below
      if (top + dropH > window.innerHeight - 12) {
        top = rect.top - dropH - 6;
      }

      // Keep within horizontal viewport bounds
      if (left < 8) left = 8;
      if (left + dropW > window.innerWidth - 8) {
        left = window.innerWidth - dropW - 8;
      }

      dropdown.style.top = `${top}px`;
      dropdown.style.left = `${left}px`;
    },

    scrollToSelected() {
      const ITEM_H = 36;

      const hourScroll = this.$refs.hourScroll;
      if (hourScroll) {
        const idx = this.validHours.indexOf(this.selectedHour);
        if (idx >= 0) {
          hourScroll.scrollTop = idx * ITEM_H - hourScroll.offsetHeight / 2 + ITEM_H / 2;
        }
      }

      const minScroll = this.$refs.minScroll;
      if (minScroll) {
        minScroll.scrollTop =
          this.selectedMinute * ITEM_H - minScroll.offsetHeight / 2 + ITEM_H / 2;
      }
    },

    selectHour(h) {
      this.selectedHour = h;
      this.emitValue();
    },

    selectMinute(m) {
      this.selectedMinute = m;
      this.emitValue();
    },

    emitValue() {
      const h = String(this.selectedHour).padStart(2, '0');
      const m = String(this.selectedMinute).padStart(2, '0');
      this.$emit('input', `${h}:${m}`);
    },
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  },
};
</script>

<style scoped>
/* Wrapper */
.tp-wrapper {
  position: relative;
  display: inline-block;
}

/* Trigger button */
.tp-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  min-width: 108px;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  color: #444;
  font-size: 0.82rem;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  user-select: none;
}

.tp-trigger:hover:not(:disabled) {
  border-color: #d6b674;
  color: #c4a55a;
  box-shadow: 0 0 0 0.2rem rgba(214, 182, 116, 0.18);
}

.tp-trigger-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tp-clock-icon {
  font-size: 0.72rem;
  color: #d6b674;
  flex-shrink: 0;
}

/* Dropdown — fixed so it escapes any overflow:hidden ancestor */
.tp-dropdown {
  position: fixed;
  z-index: 9999;
  width: 224px;
  background: white;
  border: 1px solid rgba(214, 182, 116, 0.35);
  border-radius: 12px;
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.14);
  overflow: hidden;
}

/* Header */
.tp-header {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: white;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  padding: 12px 16px;
  letter-spacing: 0.04em;
}

/* Body */
.tp-body {
  display: flex;
  align-items: flex-start;
  padding: 8px 6px 4px;
}

.tp-column {
  flex: 1;
  min-width: 0;
}

.tp-col-label {
  text-align: center;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #bbb;
  padding-bottom: 4px;
}

.tp-scroll {
  height: 162px;
  overflow-y: auto;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: rgba(214, 182, 116, 0.3) transparent;
}

.tp-scroll::-webkit-scrollbar {
  width: 4px;
}

.tp-scroll::-webkit-scrollbar-thumb {
  background: rgba(214, 182, 116, 0.35);
  border-radius: 2px;
}

/* Items */
.tp-item {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  color: #666;
  cursor: pointer;
  border-radius: 6px;
  margin: 1px 4px;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.tp-item:hover:not(.tp-item-active) {
  background: rgba(214, 182, 116, 0.12);
  color: #c4a55a;
}

.tp-item-active {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: white !important;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(214, 182, 116, 0.35);
}

/* Colon separator */
.tp-colon {
  font-size: 1.1rem;
  font-weight: 700;
  color: #ccc;
  padding: 0 2px;
  margin-top: 26px;
  flex-shrink: 0;
}

/* Footer */
.tp-footer {
  padding: 8px 12px;
  border-top: 1px solid rgba(214, 182, 116, 0.15);
  text-align: right;
}

.tp-done-btn {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 5px 18px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
}

.tp-done-btn:hover {
  opacity: 0.9;
  box-shadow: 0 2px 8px rgba(214, 182, 116, 0.4);
}

/* Fade transition */
.tp-fade-enter-active,
.tp-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.tp-fade-enter,
.tp-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
